import React from 'react';
import { NavLink } from 'react-router-dom';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  Drawer as DrawerComponent,
  Hidden,
  List,
  ListItem,
  ListItemText,
  SvgIcon,
  IconButton,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import settings from '../../components/settings';
import { useDispatch, useSelector } from 'react-redux';
import { getIsShrinkSidebar } from '../../stores/sidebar/getters';
import { toggleSidebar } from '../../stores/sidebar/actions';
import SupportButton from '../../components/SupportButton';
import { useLocation } from 'react-router-dom/';
import BookOutlinedIcon from '@material-ui/icons/BookOutlined';
import TooltipEnclosureMiddle from '../../components/TooltipEnclosureMiddle';
import { isMobile } from '../../helpers/mobile-detect';
import subscriptionAppBarItemsList from './SubscriptionAccountMenu/subscription-app-bar-items-list';

const { drawerWidth } = settings;

const subscriptionRoutes = subscriptionAppBarItemsList.map((item) => ({
  id: item.name,
  name: item.name,
  path: item.href,
  icon: item.icon,
}));

const useStyles = makeStyles((theme) => ({
  drawer: {
    [theme.breakpoints.up('md')]: {
      width: drawerWidth,
      flexShrink: 0,
    },
  },
  drawerShrink: {
    [theme.breakpoints.up('md')]: {
      width: 80,
      flexShrink: 0,
    },
  },
  list: {
    paddingTop: 7,
  },
  listShrink: {
    position: 'absolute',
    left: 0,
    bottom: 0,
    width: '100%',
    paddingBottom: theme.spacing(6),
    '& > div': {
      minHeight: '51px',
    },
  },
  listItem: {
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    paddingBottom: 0,
    minHeight: 55,

    '&.shrinkSidebar': {
      '&  a': {
        [theme.breakpoints.up('md')]: {
          paddingLeft: 28,
          paddingRight: 28,
        },
      },
    },

    '&  a': {
      paddingLeft: 16,
      paddingRight: 0,
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      minHeight: 55,
      textDecoration: 'none',
      color: 'inherit',
      display: 'flex',
      width: '100%',
      alignItems: 'center',
      position: 'relative',
    },

    '& svg': {
      fill: 'var(--color-fontColors-dark)',
      color: 'var(--color-fontColors-dark)',

      '& path': {
        fill: 'var(--color-fontColors-dark)',
        color: 'var(--color-fontColors-dark)',
      },
    },

    '&.isActive': {
      '& .MuiListItemText-root > span': {
        color: 'var(--color-colorPrimary-main)',
      },

      '& svg': {
        fill: 'var(--color-colorPrimary-main)',
        color: 'var(--color-colorPrimary-main)',

        '& path': {
          fill: 'var(--color-colorPrimary-main)',
          color: 'var(--color-colorPrimary-main)',
        },
      },
    },
  },
  listItemDocumentation: {
    paddingLeft: 0,
    paddingRight: 0,
    justifyContent: 'center',

    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  listItemText: {
    '& > span': {
      fontWeight: 500,
      fontSize: '1rem',
      marginLeft: 7,
      letterSpacing: 0,
      color: 'var(--color-fontColors-dark)',
    },
  },
  // necessary for content to be below app bar
  toolbar: theme.mixins.toolbar,
  drawerPaper: {
    width: drawerWidth,
  },
  drawerDesktop: {
    width: drawerWidth,
    zIndex: 500,
  },
  DrawerFooter: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    marginTop: 'auto',
    marginBottom: 20,

    [theme.breakpoints.up('md')]: {},
  },
  shrinkArrow: {
    justifyContent: 'flex-end',
    display: 'flex',
    '& .arrow': {
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
      }),
      marginRight: theme.spacing(3),
      transform: 'rotate(90deg)',
      // transform: 'rotate(270deg)',
    },
  },
  shrinkArrowShrink: {
    justifyContent: 'center',
    display: 'flex',
    '& .arrow': {
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
      }),
      transform: 'rotate(270deg)',
    },
  },
  drawerDesktopShrink: {
    width: 'initial',
    zIndex: 500,
    '& .MuiListItem-root': {
      minHeight: '51px',
    },
    '& .MuiListItemText-root': {
      display: 'none',
    },
  },
  TooltipHolder: {
    '&.shrinkSidebar': {
      [theme.breakpoints.up('md')]: {
        alignItems: 'center',
        display: 'flex',
        width: '100%',
        height: 'inherit',
        justifyContent: 'center',
      },
    },
  },
}));

function TooltipHolder({ title, children }) {
  const classes = useStyles();
  const shrinkSidebar = useSelector(getIsShrinkSidebar);

  if (isMobile) {
    return <>{children}</>;
  }

  if (!shrinkSidebar) {
    return <>{children}</>;
  }

  return (
    <TooltipEnclosureMiddle
      title={title}
      className={`${classes.TooltipHolder} ${
        shrinkSidebar ? 'shrinkSidebar' : ''
      }`}
    >
      {children}
    </TooltipEnclosureMiddle>
  );
}

function DrawerMenuItem({ onClick, route }) {
  const classes = useStyles();
  const shrinkSidebar = useSelector(getIsShrinkSidebar);
  const location = useLocation();
  const isActive =
    location?.pathname &&
    route?.path &&
    location?.pathname.startsWith(route?.path)
      ? true
      : false;

  return (
    <ListItem
      button
      className={`${classes.listItem} ${isActive ? 'isActive' : ''} ${
        shrinkSidebar ? 'shrinkSidebar' : ''
      }`}
    >
      <TooltipHolder title={route.name}>
        <NavLink to={route.path} activeClassName="active" onClick={onClick}>
          {route.icon ? route.icon : ''}
          <ListItemText primary={route.name} className={classes.listItemText} />
        </NavLink>
      </TooltipHolder>
    </ListItem>
  );
}

function DrawerMenuItemExternal({ route }) {
  const classes = useStyles();
  const shrinkSidebar = useSelector(getIsShrinkSidebar);

  return (
    <ListItem
      button
      className={`${classes.listItem} ${shrinkSidebar ? 'shrinkSidebar' : ''}`}
    >
      <TooltipHolder title={route.name}>
        <a href={route.path} target="_blank" rel="noopener noreferrer">
          {route.icon ? (
            <SvgIcon component={route.icon} className="drawerIcon" />
          ) : null}
          <ListItemText primary={route.name} className={classes.listItemText} />
        </a>
      </TooltipHolder>
    </ListItem>
  );
}

const DrawerMenu = ({ onItemClick }) => {
  const classes = useStyles();

  return (
    <div>
      <div className={classes.toolbar} />
      <List className={classes.list}>
        {subscriptionRoutes.map((route) => (
          <DrawerMenuItem
            button
            key={route.name}
            route={route}
            onClick={onItemClick}
          />
        ))}
      </List>
    </div>
  );
};

function DrawerFooter({ children, className }) {
  const classes = useStyles();
  const shrinkSidebar = useSelector(getIsShrinkSidebar);

  return (
    <div
      className={`DrawerFooter ${classes.DrawerFooter} ${
        shrinkSidebar ? 'shrinkSidebar' : ''
      } ${className}`}
    >
      {children}
    </div>
  );
}

function FooterContent() {
  const classes = useStyles();
  const shrinkSidebar = useSelector(getIsShrinkSidebar);

  return (
    <>
      <DrawerMenuItemExternal
        route={{
          path: 'https://quantiply.tech/documentation',
          name: 'Docs',
          icon: BookOutlinedIcon,
        }}
      />
      <ListItem
        className={`${classes.listItemDocumentation} ${
          shrinkSidebar ? 'shrinkSidebar' : ''
        }`}
      >
        <SupportButton drawer />
      </ListItem>
    </>
  );
}

const SubscriptionDrawer = ({ handleDrawerToggle, mobileOpen, routes }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const theme = useTheme();
  const shrinkSidebar = useSelector(getIsShrinkSidebar);

  const toggleShrinkSidebar = () => {
    dispatch(toggleSidebar());
  };

  return (
    <nav
      className={shrinkSidebar ? classes.drawerShrink : classes.drawer}
      id="drawer-main"
    >
      <Hidden mdUp implementation="css">
        <DrawerComponent
          variant="temporary"
          anchor={theme.direction === 'rtl' ? 'right' : 'left'}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          classes={{
            paper: classes.drawerPaper,
          }}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
        >
          <DrawerMenu routes={routes} onItemClick={handleDrawerToggle} />

          <DrawerFooter>
            <FooterContent />
          </DrawerFooter>
        </DrawerComponent>
      </Hidden>
      <Hidden smDown implementation="css">
        <DrawerComponent
          classes={{
            paper: shrinkSidebar
              ? classes.drawerDesktopShrink
              : classes.drawerDesktop,
          }}
          variant="permanent"
          open
          PaperProps={{ elevation: 3 }}
        >
          <DrawerMenu routes={routes} />
          <DrawerFooter>
            <FooterContent />
            <div
              className={
                shrinkSidebar ? classes.shrinkArrowShrink : classes.shrinkArrow
              }
            >
              <IconButton onClick={toggleShrinkSidebar} className="arrow">
                <ExpandMoreIcon />
              </IconButton>
            </div>
          </DrawerFooter>
        </DrawerComponent>
      </Hidden>
    </nav>
  );
};

export default SubscriptionDrawer;
